/**
 * TrackerSidebar: "The Margin Index" (Option B,
 * violation-remediation/design/sidebar-redesign-options.html). The tracker
 * shell's left rail as a retractable typographic index. No icons.
 *
 * THE ONE RULE, no pin, no modes, no localStorage: `/portal` exact at ≥lg is
 * DOCKED, with PortalLayout adding `lg:pl-[264px]` on that route only;
 * everywhere else, and everything below lg, is RETRACTED behind a bookmark tab
 * that summons the panel OVER content, so content never reflows. Retract on ✕,
 * scrim, ESC, any navigation. Below ~sm the overlay is min(85vw, 300px); the
 * tracker shell never mounted PortalMobileNav, so this is its sub-lg nav too.
 *
 * A11y: OVERLAID it is `role="dialog"` + `aria-modal` with `aria-expanded` on
 * the tab; DOCKED it is a plain `<aside>` landmark. Retracted content is
 * `aria-hidden` + visibility-hidden. Timing reads motion/motion.css.
 *
 * Sections: Navigate; Cases (every company case by building, rows deep-link
 * to the building CASE surface, never /portal/chat, FR-11); Ask CodeClear · AI (TrackerSidebarAsk.tsx; the thread list is
 * behind CHAT_ENABLED, the Chat row is not); Foot (identity popover + the GLOBAL theme toggle, which the
 * Dashboard header no longer renders).
 *
 * Every surface reads the CSS-var token layer, so the index flips with the
 * /portal dark toggle. Zero hardcoded palette.
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Link, useLocation, useRouterState } from "@tanstack/react-router";
import { useMeQuery } from "@/queries/queries";
import { logout } from "@/api/auth";
import BuildStamp from "@/components/common/BuildStamp";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shared/ui/popover";
import { cn } from "@/lib/utils";
import { usePortalTheme } from "./portalTheme";
import { FadeIn, useResolvedPathname } from "./motion";
import { CHAT_ENABLED } from "./chatEnabled";
import {
  IndexGroupRule,
  SidebarCasesSection,
  groupMetaClass,
  groupTitleClass,
  navRowClass,
  useActiveBuildingId,
} from "./TrackerSidebarCases";
import { AskCodeClearSection, ChatNavLink } from "./TrackerSidebarAsk";
import { openCaseCount } from "./caseCounts";
import { usePortfolioCasesQuery } from "./usePortfolioCasesQuery";
import { ACTION_PLAN_PATH } from "./actionPlanLink";
import { usePortalFeatures } from "./usePortalFeatures";

// ---------------------------------------------------------------------------
// Route + viewport predicates.
// ---------------------------------------------------------------------------

/**
 * The ONLY route where the index docks open at ≥lg, and where PortalLayout
 * applies the 264px offset. Exported so layout and sidebar cannot disagree
 * about what "the dashboard" is.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const isPortalDashboardPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, "") === "/portal";

const LG_MEDIA_QUERY = "(min-width: 1024px)";

/** Guarded for jsdom (matchMedia may be a stub) + SSR safety. */
const getLgMediaQueryList = (): MediaQueryList | null => {
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function"
    ) {
      return window.matchMedia(LG_MEDIA_QUERY);
    }
  } catch {
    /* fall through */
  }
  return null;
};

const subscribeLgUp = (onChange: () => void): (() => void) => {
  const mql = getLgMediaQueryList();
  if (!mql) return () => {};
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }
  // Legacy Safari (<14) fallback.
  if (typeof mql.addListener === "function") {
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }
  return () => {};
};

const readLgUp = (): boolean => getLgMediaQueryList()?.matches ?? false;

/**
 * Live ≥lg flag, for STATE a media query cannot express only: dialog
 * semantics, aria-hidden, clearing a stale summon when the dashboard docks on
 * resize. The docked GEOMETRY stays pure CSS (`lg:` variants) so jsdom class
 * assertions are viewport-independent.
 */
const useIsLgUp = (): boolean =>
  useSyncExternalStore(subscribeLgUp, readLgUp, () => false);

// ---------------------------------------------------------------------------
// Theme toggle: THE global light/dark control (the Dashboard header no longer
// renders one). Reuses portalTheme.ts; a glyph, not Sun/Moon icons, to keep
// the index icon-free. Keeps the ``portal-theme-toggle`` testid: exactly one
// control exists now, and it lives here.
// ---------------------------------------------------------------------------

const IndexThemeToggle: React.FC = () => {
  const { theme, toggle } = usePortalTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title="Switch light / dark"
      data-testid="portal-theme-toggle"
      className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg border border-ink-line text-ink-mute motion-tint hover:border-ink-line-strong hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong"
    >
      <span aria-hidden="true" className="text-[13px] leading-none">
        ◐
      </span>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Foot: identity + theme. The Settings / Filing View / Log out popover (from
// ``PortalNav.NavUserMenu``) stays on the identity block; rows are text-only.
// ---------------------------------------------------------------------------

const IndexFoot: React.FC = () => {
  const { data: user } = useMeQuery();

  // CONTROLLED SO NAVIGATION CLOSES IT (Leo, 2026-09-28). Every row here is a
  // client-side Link, the click lands INSIDE the content so Radix's
  // outside-click never fires, and the rail outlives the route: the menu
  // stayed open on top of the page it had just navigated to. Keyed on the
  // pathname, not per-row, so a row added later cannot reintroduce it.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { pathname } = useLocation();
  React.useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Email first (the design's `.e` line); display name only when it is
  // missing.
  const identity =
    user?.email ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    "Account";
  // TODO(tracker-shell): derive role from user.company.role or similar.
  const role = "Property Manager";

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-line px-5 py-4">
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="min-w-0 text-left motion-tint hover:opacity-70"
            data-testid="tracker-user-pill"
          >
            <div className="truncate text-2xs text-ink">{identity}</div>
            <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-faint">
              {role}
            </div>
          </button>
        </PopoverTrigger>
        {/* z-[80] CLEARS THE RAIL'S OWN z-[70] (:712). Radix copies the
            content's computed z-index onto its portaled wrapper, and the
            shadcn default is z-50, so the popover opened BEHIND the opaque
            sidebar: `elementFromPoint` over the Settings row returned the
            rail's scroll div, making the menu invisible and unclickable at
            every viewport. Measured 2026-09-27. */}
        <PopoverContent
          side="top"
          align="start"
          className="motion-pop z-[80] w-44 p-1.5"
          // Close on the click itself, so the menu never exits over the next page.
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a")) setMenuOpen(false);
          }}
        >
          {/* SETTINGS STAYS IN THE TRACKER (Leo, 2026-09-27: "I would like to
              access the settings thru clicking on profile and choosing
              Settings"). It used to point at /settings, the legacy filings
              shell, which left the app entirely. Classic settings is still
              reachable at /settings directly and through Filing View below. */}
          <Link
            to="/portal/settings"
            className="block rounded-md px-3 py-2 text-sm text-ink-body motion-tint hover:bg-paper-panel"
          >
            Settings
          </Link>
          {/* Filing View is an is_staff-only escape hatch into the Classic
              Filing app shell. Tracker customers never see it: that surface
              is not meant for their workflow. */}
          {user?.isStaff && (
            <Link
              to="/"
              className="block rounded-md px-3 py-2 text-sm text-ink-body motion-tint hover:bg-paper-panel"
            >
              Filing View
            </Link>
          )}
          <div className="my-1 border-t border-ink-line" />
          <button
            type="button"
            onClick={handleLogout}
            className="block w-full rounded-md px-3 py-2 text-left text-sm text-destructive motion-tint hover:bg-destructive/10"
          >
            Log out
          </button>
        </PopoverContent>
      </Popover>
      <IndexThemeToggle />
    </div>
  );
};

// Inline: the arbitrary ease-[...] class lost to transition-*'s default curve.
const INDEX_TIMING: React.CSSProperties = {
  transitionDuration: "var(--motion-slow)",
  transitionTimingFunction: "var(--ease-out)",
};

const TrackerSidebar: React.FC = () => {
  // Dock and retract both read the resolved location: on the pending one, a
  // Dashboard click at lg slid the overlay out, then docked it back in.
  const href = useRouterState({
    select: (s) => (s.resolvedLocation ?? s.location).href,
  });
  const onDashboard = isPortalDashboardPath(useResolvedPathname());
  const lgUp = useIsLgUp();
  /** Docked = the dashboard's ≥lg always-open state (the only one). */
  const docked = onDashboard && lgUp;

  // ``summoned`` is the only UI state here, and deliberately NOT persisted:
  // one gesture in, one gesture out.
  const [summoned, setSummoned] = useState(false);
  const overlaid = summoned && !docked;
  const retracted = !summoned && !docked;

  const tabRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const activeBuildingId = useActiveBuildingId();
  const actionPlanOn = usePortalFeatures().actionPlan;
  // The AI→cases link needs a case on this building: with none the page has no
  // ``#managed-cases`` anchor. Same key as SidebarCasesSection: one fetch.
  const { data: railCases } = usePortfolioCasesQuery();
  const hasManagedCases = !!railCases?.some(
    (c) => c.buildingId === activeBuildingId,
  );
  // The company count, off a building page only: there the Case tab prints the
  // building's own (2 on 20-11 18th St), and a 9 beside it reads as "9 here".
  const tabCount = activeBuildingId ? 0 : (openCaseCount(railCases) ?? 0);
  const tabLabel = `${tabCount} open ${tabCount === 1 ? "case" : "cases"}`;

  const summon = useCallback(() => setSummoned(true), []);
  const retract = useCallback((opts?: { refocus?: boolean }) => {
    setSummoned(false);
    // Focus returns to the tab only on an explicit user retract (✕ /
    // scrim / ESC): route-driven retracts let the destination own focus.
    if (opts?.refocus) tabRef.current?.focus();
  }, []);

  // ANY navigation retracts, pathname OR search: clicking a conversation row
  // on /portal/chat only changes ?threadId.
  useEffect(() => {
    setSummoned(false);
  }, [href]);

  // A sub-lg summon carried across the lg boundary on the dashboard becomes
  // docked; drop it, or resizing back down leaves a surprise overlay.
  useEffect(() => {
    if (docked) setSummoned(false);
  }, [docked]);

  // While overlaid: move focus into the panel, retract on ESC, lock page scroll.
  useEffect(() => {
    if (!overlaid) return undefined;
    // TRAP: focus the ✕ now AND next frame. At transition progress 0
    // `visibility` still computes `hidden` (the flip rides the transition) and
    // focus() on a visibility-hidden subtree is silently ignored; one painted
    // frame later it works. The immediate call keeps jsdom synchronous.
    const focusClose = () => closeRef.current?.focus();
    focusClose();
    const rafId =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(focusClose)
        : null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSummoned(false);
        tabRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      document.removeEventListener("keydown", onKeyDown);
      body.style.overflow = prevOverflow;
    };
  }, [overlaid]);

  return (
    <>
      {/* Bookmark tab: the index's only permanent chrome. Hidden at ≥lg on
          the dashboard, where the panel docks.

          Z-ORDER: tab 30 < scrim 60 < panel 70, and BOTH SIT ABOVE 50.
          `BuildingPageHeader` is `relative z-50` and renders after this in the
          DOM, so at the old scrim 40 / panel 50 it won the tie and painted over
          the index: the header band stayed undimmed and its own width covered
          the panel from x192 to x264, clipping the CASES badge (measured 2026-
          09-22, 1600, Related). A summoned overlay is the topmost thing on the
          page, so it clears every z-50 on a detail route. */}
      <button
        ref={tabRef}
        type="button"
        onClick={summon}
        aria-expanded={overlaid}
        aria-controls="tracker-margin-index"
        aria-label="Open the index"
        data-testid="tracker-index-tab"
        className={cn(
          "fixed left-0 top-5 z-30 flex flex-col items-center gap-2 rounded-r-[10px] border border-l-0 border-ink-line bg-paper-panel px-1.5 pb-2.5 pt-3 shadow-sm motion-tint hover:border-ink-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong",
          onDashboard && "lg:hidden",
        )}
      >
        {/* The cube mark: an object-left crop of the 120×32 logo SVG. */}
        <img
          src="/codeClearLogo.svg"
          alt=""
          aria-hidden="true"
          className="h-5 w-5 object-cover object-left dark:brightness-0 dark:invert"
        />
        <span
          aria-hidden="true"
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute [writing-mode:vertical-rl]"
        >
          Index
        </span>
        {tabCount > 0 && (
          <FadeIn
            as="span"
            data-testid="tracker-index-tab-count"
            aria-label={tabLabel}
            title={tabLabel}
            className="flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[hsl(var(--teal))] px-1 font-mono text-[8.5px] font-bold leading-none text-paper"
          >
            {tabCount}
          </FadeIn>
        )}
      </button>

      {/* Scrim: ink at 25%, so it flips with the theme. Click retracts. Kept
          mounted so the fade plays both ways. */}
      <div
        data-testid="tracker-index-scrim"
        aria-hidden="true"
        onClick={() => retract({ refocus: true })}
        className={cn(
          "fixed inset-0 z-[60] bg-ink/25 motion-tint",
          overlaid ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        style={INDEX_TIMING}
      />

      {/* The index panel. Transform-driven: docked open on the dashboard at
          ≥lg (pure CSS), summoned OVER content everywhere else. */}
      <aside
        id="tracker-margin-index"
        data-testid="tracker-sidebar"
        data-state={docked ? "docked" : summoned ? "overlaid" : "retracted"}
        role={overlaid ? "dialog" : undefined}
        aria-modal={overlaid ? "true" : undefined}
        aria-label={overlaid ? "CodeClear index" : undefined}
        aria-hidden={retracted ? "true" : undefined}
        className={cn(
          "fixed inset-y-0 left-0 z-[70] flex w-[min(85vw,300px)] flex-col border-r border-ink-line bg-paper-panel sm:w-[264px]",
          // TRAP: visibility transitions on the RETRACT direction only. The
          // delayed flip keeps the slide-out visible, then the panel goes
          // unfocusable. On summon it must NOT ride the transition: at
          // progress 0 it still computes `hidden` and swallows the focus move.
          summoned
            ? "visible translate-x-0 transition-transform"
            : "invisible -translate-x-full transition-[transform,visibility]",
          onDashboard && "lg:visible lg:translate-x-0",
        )}
        style={INDEX_TIMING}
      >
        {/* Head: cube mark (2026-05-06 logo ask) + the Newsreader Code·Clear
            lockup, linking home. ✕ is hidden while docked: a docked index
            cannot close. */}
        <div className="flex items-center justify-between gap-3 border-b border-ink-line px-5 pb-4 pt-5">
          <Link
            to="/portal"
            aria-label="CodeClear — go to the dashboard"
            data-testid="tracker-brand-home"
            className="flex min-w-0 items-center gap-2"
          >
            <img
              src="/codeClearLogo.svg"
              alt=""
              aria-hidden="true"
              className="h-6 w-6 shrink-0 object-cover object-left dark:brightness-0 dark:invert"
            />
            <span className="truncate font-display text-[17px] leading-none text-ink">
              Code
              <em className="not-italic text-[hsl(var(--teal))]">Clear</em>
            </span>
          </Link>
          <button
            ref={closeRef}
            type="button"
            onClick={() => retract({ refocus: true })}
            aria-label="Close the index"
            data-testid="tracker-index-close"
            className={cn(
              "shrink-0 font-mono text-[11px] uppercase tracking-[0.12em] text-ink-faint motion-tint hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong",
              onDashboard && "lg:hidden",
            )}
          >
            Close ✕
          </button>
        </div>

        {/* Navigate, then the two help SPECIES. Case rows link to the building
            CASE SURFACE, AI rows to /portal/chat. FE-merge only: nothing
            bridges them, so the AI cannot post into a case thread. The
            native bar stays visible, dark in dark mode (Show all, 11-01). */}
        <div className="flex-1 overflow-y-auto overscroll-contain pb-5 dark:[color-scheme:dark]">
          {/* NAVIGATE: active state = weight, not color blocks. */}
          <nav
            aria-label="Portal"
            data-testid="tracker-nav-section"
            className="px-5 pb-1 pt-4"
          >
            <IndexGroupRule
              left={<h3 className={groupTitleClass}>Navigate</h3>}
            />
            <Link
              to="/portal"
              activeOptions={{ exact: true }}
              className={navRowClass}
              activeProps={{ className: "font-semibold" }}
            >
              <span>Dashboard</span>
              <span className={groupMetaClass}>Portfolio</span>
            </Link>
            {actionPlanOn && (
              <Link
                to={ACTION_PLAN_PATH}
                className={navRowClass}
                activeProps={{ className: "font-semibold" }}
                data-testid="tracker-nav-action-plan"
              >
                <span>Action plan</span>
                <span className={groupMetaClass}>Portfolio</span>
              </Link>
            )}
            {/* Leo 2026-10-01: Chat stays reachable; the page shows Under Construction. */}
            <ChatNavLink />
          </nav>

          <SidebarCasesSection activeBuildingId={activeBuildingId} />

          {CHAT_ENABLED && (
            <AskCodeClearSection
              activeBuildingId={activeBuildingId}
              hasManagedCases={hasManagedCases}
            />
          )}
        </div>

        <IndexFoot />
        {/* Build stamp: THE ONLY MOUNT IN THE APP, staff-gated, and the only
            line naming the build during a stale-image incident. Recorded
            nowhere else: staff in the Classic shell (/buildings, /filings,
            /tracking) have NO in-browser version readout and must read
            /api/version/ directly. Renders nothing when neither the bundle nor
            the API knows its build. */}
        <BuildStamp className="px-5 pb-4" />
      </aside>
    </>
  );
};

export default TrackerSidebar;
